import { useState } from 'react';
import './AboutPage.css';

const AboutPage = ({ onClose, initialSection }) => {
  const [expandedSections, setExpandedSections] = useState({
    introduction: initialSection === 'introduction' || (!initialSection && true),
    esoteric: initialSection === 'esoteric',
    higherOctave: initialSection === 'higherOctave',
    trueAngles: initialSection === 'trueAngles',
    resonance: initialSection === 'resonance',
    geocentricVsHeliocentric: initialSection === 'geocentricVsHeliocentric',
    applications: initialSection === 'applications',
    astronomyAstrology: initialSection === 'astronomyAstrology',
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'about-page-overlay' && onClose) {
      onClose();
    }
  };

  return (
    <div className="about-page-overlay" onClick={handleOverlayClick}>
      <div className="about-page" style={{ backgroundImage: "url('/BGForDialog.webp')" }}>
        <div className="about-page__header">
          <h1>About Heliocentric Astrology</h1>
          {onClose && (
            <button className="about-page__close" onClick={onClose} aria-label="Close">
              ×
            </button>
          )}
        </div>

        <div className="about-page__content">
          <section
            className={`about-section ${expandedSections.introduction ? 'expanded' : ''}`}
          >
            <button
              className="about-section__header"
              onClick={() => toggleSection('introduction')}
              aria-expanded={expandedSections.introduction}
            >
              <h2>Introduction to Heliocentric Astrology</h2>
              <span className="about-section__toggle">
                {expandedSections.introduction ? '−' : '+'}
              </span>
            </button>
            {expandedSections.introduction && (
              <div className="about-section__content">
                <p>
                  Heliocentric astrology offers a profound shift in perspective, viewing planetary
                  positions from the Sun's center rather than Earth's. This approach reveals deeper,
                  more spiritual dimensions of astrological understanding that complement traditional
                  geocentric astrology.
                </p>
                <p>
                  While geocentric astrology shows us how we appear in the world and how we interact
                  with our environment, heliocentric astrology reveals the soul's deeper purpose and
                  the divine blueprint encoded in our cosmic DNA. Together, they provide a complete
                  picture: geocentric shows the vehicle (personality), while heliocentric shows the
                  driver (soul).
                </p>
              </div>
            )}
          </section>

          <section
            className={`about-section ${expandedSections.esoteric ? 'expanded' : ''}`}
          >
            <button
              className="about-section__header"
              onClick={() => toggleSection('esoteric')}
              aria-expanded={expandedSections.esoteric}
            >
              <h2>Esoteric and Spiritual Meaning</h2>
              <span className="about-section__toggle">
                {expandedSections.esoteric ? '−' : '+'}
              </span>
            </button>
            {expandedSections.esoteric && (
              <div className="about-section__content">
                <p>
                  Heliocentric astrology provides access to a deeper, more spiritual or soul-level
                  understanding of one's cosmic blueprint. Unlike geocentric astrology, which reflects
                  personality and incarnate experience, heliocentric charts reveal the{' '}
                  Higher Self or soul intention behind the life.
                </p>
                <p>
                  This perspective connects us to the divine blueprint of a being's evolution, showing
                  not just how we appear in the world, but why we chose to incarnate and what our
                  soul seeks to express. It answers questions about our deeper purpose, karmic
                  patterns, and the evolutionary missions we've undertaken.
                </p>
                <p>
                  In metaphysical healing and spiritual mapping, heliocentric astrology can be used
                  to identify soul frequencies, map mission codes, heal karmic patterns, and connect
                  with higher guidance from the soul's perspective rather than the ego's.
                </p>
              </div>
            )}
          </section>

          <section
            className={`about-section ${expandedSections.higherOctave ? 'expanded' : ''}`}
          >
            <button
              className="about-section__header"
              onClick={() => toggleSection('higherOctave')}
              aria-expanded={expandedSections.higherOctave}
            >
              <h2>Higher Octave and Divine Blueprint</h2>
              <span className="about-section__toggle">
                {expandedSections.higherOctave ? '−' : '+'}
              </span>
            </button>
            {expandedSections.higherOctave && (
              <div className="about-section__content">
                <p>
                  The heliocentric model represents a "higher octave" of astrological understanding.
                  It includes not only the traditional planets but also dwarf planets and asteroids
                  such as Ceres, Pallas, Juno, Vesta, Chiron, Pholus (from Swiss Ephemeris), and
                  Makemake, Eris, Quaoar, Sedna, Haumea, Ixion, Gonggong, and Orcus (from NASA JPL
                  Horizons API).
                </p>
                <p>
                  These celestial bodies operate at frequencies that speak to the soul's deeper
                  purposes, karmic patterns, and evolutionary missions. They reveal the divine blueprint
                  encoded in our cosmic DNA, showing how we are designed to contribute to collective
                  evolution.
                </p>
                <p>
                  Each of these "higher octave" bodies carries specific frequencies and lessons:
                </p>
                <ul>
                  <li>
                    <strong>Ceres</strong>: Nurturing, cycles of growth and harvest, connection to
                    Earth
                  </li>
                  <li>
                    <strong>Pallas</strong>: Wisdom, strategic thinking, creative intelligence
                  </li>
                  <li>
                    <strong>Juno</strong>: Partnership, commitment, sacred contracts
                  </li>
                  <li>
                    <strong>Vesta</strong>: Devotion, focus, sacred service
                  </li>
                  <li>
                    <strong>Chiron</strong>: Wounding and healing, the wounded healer archetype
                  </li>
                  <li>
                    <strong>Pholus</strong>: Sudden transformation, catalyst for change
                  </li>
                  <li>
                    <strong>Makemake</strong>: Authentic self-creation, natural law, living in alignment with one’s true nature
                  </li>
                  <li>
                    <strong>Eris</strong>: Sacred disruption, truth through chaos, exposing false harmony and injustice
                  </li>
                  <li>
                    <strong>Quaoar</strong>: Order emerging from chaos, the power of ritual, conscious creation
                  </li>
                  <li>
                    <strong>Sedna</strong>: Deep abandonment wounds, betrayal, survival instincts, soul-level resilience
                  </li>
                  <li>
                    <strong>Haumea</strong>: Rapid regeneration, rebirth, creative life force, feminine power of renewal
                  </li>
                  <li>
                    <strong>Ixion</strong>: Moral testing, abuse of power, consequences of crossing sacred boundaries
                  </li>
                  <li>
                    <strong>Varuna</strong>: Cosmic justice, moral authority, divine order, unseen accountability
                  </li>
                  <li>
                    <strong>Gonggong</strong>: Emotional extremity, destabilization of systems, flooding suppressed truths
                  </li>
                  <li>
                    <strong>Orcus</strong>: Oaths, promises, karmic contracts, consequences for broken word
                  </li>

                </ul>
              </div>
            )}
          </section>

          <section
            className={`about-section ${expandedSections.trueAngles ? 'expanded' : ''}`}
          >
            <button
              className="about-section__header"
              onClick={() => toggleSection('trueAngles')}
              aria-expanded={expandedSections.trueAngles}
            >
              <h2>True Planetary Angles</h2>
              <span className="about-section__toggle">
                {expandedSections.trueAngles ? '−' : '+'}
              </span>
            </button>
            {expandedSections.trueAngles && (
              <div className="about-section__content">
                <p>
                  One of the most significant advantages of heliocentric astrology is that it isn't
                  affected by Earth's motion. This means it shows true angles between
                    planets-how Venus truly relates to Mars, or how Jupiter relates to
                  Saturn, without the distortion of Earth's orbital perspective.
                </p>
                <p>
                  This clarity is essential for astrologers who study planetary resonance, solar system
                  harmonics, or the geometry of consciousness. The relationships revealed are pure and
                  direct, reflecting the actual cosmic dance of the solar system.
                </p>
                <p>
                  For example, a heliocentric aspect between Venus and Mars shows the true energetic
                  relationship between these planets, unmediated by Earth's position. This provides
                  clearer insight into how these energies interact at the soul level, beyond the
                  personality-level expressions seen in geocentric charts.
                </p>
              </div>
            )}
          </section>

          <section
            className={`about-section ${expandedSections.resonance ? 'expanded' : ''}`}
          >
            <button
              className="about-section__header"
              onClick={() => toggleSection('resonance')}
              aria-expanded={expandedSections.resonance}
            >
              <h2>Planetary Resonance and Harmonics</h2>
              <span className="about-section__toggle">
                {expandedSections.resonance ? '−' : '+'}
              </span>
            </button>
            {expandedSections.resonance && (
              <div className="about-section__content">
                <p>
                  Heliocentric astrology is particularly valuable for understanding planetary resonance
                  and solar system harmonics. By removing Earth's perspective, we can see the true
                  geometric relationships that govern cosmic energy patterns.
                </p>
                <p>This helps identify:</p>
                <ul>
                  <li>
                    <strong>Resonant frequencies</strong> between planets that create specific
                    energetic signatures
                  </li>
                  <li>
                    <strong>Harmonic patterns</strong> that influence consciousness and collective
                    evolution
                  </li>
                  <li>
                    <strong>Geometric configurations</strong> that activate specific soul lessons and
                    evolutionary tasks
                  </li>
                  <li>
                    <strong>Cosmic rhythms</strong> that guide evolutionary processes and collective
                    transformation
                  </li>
                </ul>
                <p>
                  These patterns are essential for understanding how planetary energies work together to
                  create specific frequencies of consciousness and experience at the soul level.
                </p>
              </div>
            )}
          </section>

          <section
            className={`about-section ${expandedSections.geocentricVsHeliocentric ? 'expanded' : ''}`}
          >
            <button
              className="about-section__header"
              onClick={() => toggleSection('geocentricVsHeliocentric')}
              aria-expanded={expandedSections.geocentricVsHeliocentric}
            >
              <h2>Geocentric vs Heliocentric</h2>
              <span className="about-section__toggle">
                {expandedSections.geocentricVsHeliocentric ? '−' : '+'}
              </span>
            </button>
            {expandedSections.geocentricVsHeliocentric && (
              <div className="about-section__content">
                <p>
                  The distinction between these two perspectives is fundamental to understanding the
                  full spectrum of astrological meaning:
                </p>
                <div className="comparison-grid">
                  <div className="comparison-item">
                    <h3 style={{ textAlign: 'center' }}>Geocentric (Earth-centered)</h3>
                    <ul>
                      <li>Reflects personality and incarnate experience</li>
                      <li>Shows how we show up in daily life</li>
                      <li>Reveals our relationship with the material world</li>
                      <li>Describes the vehicle through which the soul expresses</li>
                      <li>Focuses on personal identity and ego structure</li>
                    </ul>
                  </div>
                  <div className="comparison-item">
                    <h3 style={{ textAlign: 'center' }}>Heliocentric (Sun-centered)</h3>
                    <ul>
                      <li>Reveals higher self and soul intention</li>
                      <li>Shows spiritual purpose and deeper meaning</li>
                      <li>Connects to the divine blueprint of evolution</li>
                      <li>Describes the driver behind the personality</li>
                      <li>Focuses on soul mission and evolutionary purpose</li>
                    </ul>
                  </div>
                </div>
                <p>
                  Together, they provide a complete picture: geocentric shows the vehicle (personality),
                  while heliocentric shows the driver (soul). Both perspectives are valuable and
                  complementary, offering different layers of understanding.
                </p>
              </div>
            )}
          </section>

          <section
            className={`about-section ${expandedSections.applications ? 'expanded' : ''}`}
          >
            <button
              className="about-section__header"
              onClick={() => toggleSection('applications')}
              aria-expanded={expandedSections.applications}
            >
              <h2>Applications</h2>
              <span className="about-section__toggle">
                {expandedSections.applications ? '−' : '+'}
              </span>
            </button>
            {expandedSections.applications && (
              <div className="about-section__content">
                <h3>Scientific and Technological Applications</h3>
                <p>
                  Heliocentric astrology naturally aligns with scientific understanding since it mirrors
                  the true solar system structure. This makes it particularly insightful for:
                </p>
                <ul>
                  <li>
                    <strong>Scientific minds</strong> who appreciate objective, universal mechanics
                  </li>
                  <li>
                    <strong>Technological innovators</strong> who work with systems and patterns
                  </li>
                  <li>
                    <strong>Researchers</strong> studying cosmic influences on consciousness
                  </li>
                  <li>
                    <strong>Astronomers</strong> bridging the gap between astronomy and astrology
                  </li>
                </ul>

                <h3>Astrocartography and Solar Dynamics</h3>
                <p>The heliocentric model is essential for:</p>
                <ul>
                  <li>
                    <strong>Astrocartography</strong>: Mapping global and cosmic energy patterns
                    across geographical locations
                  </li>
                  <li>
                    <strong>Solar dynamics</strong>: Understanding how solar system patterns influence
                    planetary and collective consciousness
                  </li>
                  <li>
                    <strong>Global energy mapping</strong>: Identifying areas where specific cosmic
                    frequencies are most active
                  </li>
                </ul>

                <h3>Metaphysical Healing and Spiritual Mapping</h3>
                <p>In metaphysical healing and spiritual mapping, heliocentric astrology can be used to:</p>
                <ul>
                  <li>
                    <strong>Identify soul frequencies</strong>: Discover the vibrational patterns that
                    resonate with one's essence
                  </li>
                  <li>
                    <strong>Map mission codes</strong>: Understand the encoded purposes and
                    evolutionary tasks
                  </li>
                  <li>
                    <strong>Heal karmic patterns</strong>: Recognize and transform deep soul-level
                    imprints
                  </li>
                  <li>
                    <strong>Connect with higher guidance</strong>: Access information from the soul's
                    perspective rather than the ego's
                  </li>
                </ul>
              </div>
            )}
          </section>

          <section
            className={`about-section ${expandedSections.astronomyAstrology ? 'expanded' : ''}`}
          >
            <button
              className="about-section__header"
              onClick={() => toggleSection('astronomyAstrology')}
              aria-expanded={expandedSections.astronomyAstrology}
            >
              <h2>Astronomy and Astrology: A Natural Bridge</h2>
              <span className="about-section__toggle">
                {expandedSections.astronomyAstrology ? '−' : '+'}
              </span>
            </button>
            {expandedSections.astronomyAstrology && (
              <div className="about-section__content">
                <p>
                  Heliocentric astrology naturally links astronomy and astrology, since it mirrors the
                  true solar system structure. This alignment allows for:
                </p>
                <ul>
                  <li>
                    <strong>Reconciliation of symbolic and astrophysical</strong>: Connecting
                    metaphysical understanding with cosmic order
                  </li>
                  <li>
                    <strong>Scientific validation</strong>: Using actual planetary positions and
                    relationships
                  </li>
                  <li>
                    <strong>Objective framework</strong>: Working with measurable, observable cosmic
                    patterns
                  </li>
                  <li>
                    <strong>Holistic understanding</strong>: Integrating both empirical and intuitive
                    knowledge
                  </li>
                </ul>
                <p>
                  Some researchers and energy workers view heliocentric astrology as a way to reconcile
                  the symbolic with the astrophysical, connecting metaphysical understanding with
                  cosmic order. It represents an interpretation of ancient wisdom and modern scientific
                  understanding, offering a bridge between the seen and unseen dimensions of reality.
                </p>
                <p>
                  This approach honors both the empirical precision of astronomy and the symbolic
                  depth of astrology, creating a holistic framework that speaks to both the rational
                  mind and the intuitive heart.
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );

};

export default AboutPage;

